import {combineReducers} from 'redux';
import builderDsdListReducer from './dsd-list/reducer';
import {REQUEST_ERROR, REQUEST_START, REQUEST_SUCCESS} from '../../../middlewares/api/actions';
import {BUILDER_CUBE_FORM_CUBE_READ, BUILDER_CUBE_FORM_SUBMIT} from './cube-form/actions';
import {DCS_ORDERED_TREE_ROOT, getCubesTree} from "../../../utils/treeBuilders";
import {
  getFilteredTreeWithPaths,
  getMappedTree,
  getNodes,
  UNCATEGORIZED_CATEGORY_CODE
} from "../../../utils/tree";
import {
  DEFAULT_CATEGORY_SCHEME_SELECTOR_CREATE_SUBMIT,
  DEFAULT_CATEGORY_SCHEME_SELECTOR_SELECT_SUBMIT
} from "../../../middlewares/default-category-scheme-selector/actions";
import {
  BUILDER_CUBE_TREE_CATEGORISED_CUBES_READ,
  BUILDER_CUBE_TREE_CATEGORY_DELETE,
  BUILDER_CUBE_TREE_CATEGORY_MOVE,
  BUILDER_CUBE_TREE_CUBE_CATEGORY_SELECT,
  BUILDER_CUBE_TREE_CUBE_CREATE,
  BUILDER_CUBE_TREE_CUBE_DELETE,
  BUILDER_CUBE_TREE_CUBE_SELECT
} from "./cube-tree/actions";
import {
  BUILDER_CATEGORY_FORM_CHANGE,
  BUILDER_CATEGORY_FORM_CREATE,
  BUILDER_CATEGORY_FORM_CREATE_SUBMIT,
  BUILDER_CATEGORY_FORM_EDIT_SUBMIT,
  BUILDER_CATEGORY_FORM_HIDE,
  BUILDER_CATEGORY_FORM_PARENT_MODAL_HIDE,
  BUILDER_CATEGORY_FORM_PARENT_MODAL_SHOW
} from "./category-form/actions";
import builderCubeFormReducer from "./cube-form/reducer";
import {getCurrentUserPermissions} from "../../../middlewares/current-user-permissions/middleware";
import {getCurrentNodeConfig} from "../../../middlewares/current-node-config/middleware";

const builderReducer = combineReducers({
  components: combineReducers({
    dsdList: builderDsdListReducer,
    cubeForm: builderCubeFormReducer
  }),
  shared: (
    state = {
      cubeId: null,
      category: null,
      categorisedCubes: null,
      isCategoryFormParentModalVisible: false,
      forbiddenCubesCodes: null,
      forbiddenCategoriesCodes: null
    },
    action
  ) => {
    switch (action.type) {
      case BUILDER_CATEGORY_FORM_PARENT_MODAL_SHOW:
        return {
          ...state,
          isCategoryFormParentModalVisible: true
        };
      case BUILDER_CATEGORY_FORM_PARENT_MODAL_HIDE:
        return {
          ...state,
          isCategoryFormParentModalVisible: false
        };
      case BUILDER_CUBE_TREE_CUBE_SELECT: {
        return {
          ...state,
          cubeId: action.cubeId
        }
      }
      case BUILDER_CUBE_TREE_CUBE_CATEGORY_SELECT: {
        return {
          ...state,
          category: action.category
        }
      }
      case BUILDER_CATEGORY_FORM_CREATE:
        return {
          ...state,
          category: {
            IDCat: null,
            ParCode: action.parCode,
            Ord: null,
            labels: null
          }
        };
      case BUILDER_CUBE_TREE_CUBE_CREATE:
        return {
          ...state,
          category: null
        };
      case BUILDER_CATEGORY_FORM_CHANGE:
        return {
          ...state,
          category: {
            ...state.category,
            ...action.fields
          }
        };
      case BUILDER_CATEGORY_FORM_HIDE:
        return {
          ...state,
          category: null
        };
      case REQUEST_START:
        switch (action.label) {
          case BUILDER_CUBE_TREE_CATEGORISED_CUBES_READ:
            return {
              ...state,
              categorisedCubes: null
            };
          default:
            return state;
        }
      case REQUEST_ERROR:
        switch (action.label) {
          case BUILDER_CUBE_FORM_CUBE_READ:
            return {
              ...state,
              cubeId: null
            };
          case BUILDER_CUBE_TREE_CATEGORISED_CUBES_READ:
            return {
              ...state,
              categorisedCubes: []
            };
          default:
            return state;
        }
      case REQUEST_SUCCESS: {
        switch (action.label) {
          case BUILDER_CUBE_FORM_SUBMIT: {
            return {
              ...state,
              cubeId: action.response,
              category: null,
              categorisedCubes: null
            }
          }
          case BUILDER_CUBE_TREE_CUBE_DELETE:
            return {
              ...state,
              cubeId: null,
              categorisedCubes: null
            };
          case BUILDER_CUBE_TREE_CATEGORISED_CUBES_READ:

            const permissions = getCurrentUserPermissions(action);

            const isAdmin =
              permissions && permissions.rules && permissions.rules.includes("AdminRole");

            const canViewForbiddenObjects =
              getCurrentNodeConfig(action).general.canViewUnallowedBuilderNodes;

            const [allCubes, dcs] = action.response;

            const forbiddenCubesCodes =
              allCubes
                .filter(({Code}) =>
                  !permissions ||
                  (!permissions.cube.includes(Code) && !permissions.cubeOwner.includes(Code)))
                .map(({Code}) => Code);

            const allowedCategorisedCubesTree = getCubesTree(
              allCubes.filter((({Code}) =>
                canViewForbiddenObjects || !forbiddenCubesCodes.includes(Code))),
              dcs
            );

            const forbiddenCategoriesCodes =
              getNodes(
                allowedCategorisedCubesTree,
                "children",
                ({CatCode}) =>
                  !permissions ||
                  (CatCode !== undefined && permissions.categories.indexOf(CatCode) < 0)
              )
                .map(({CatCode}) => CatCode)
                .filter(catCode => catCode !== DCS_ORDERED_TREE_ROOT.CatCode && catCode !== UNCATEGORIZED_CATEGORY_CODE);

            const allowedCategorisedCubesWithOnlyAllowedCategoriesTree = getFilteredTreeWithPaths(
              allowedCategorisedCubesTree,
              "children",
              obj => obj.Code || isAdmin || !forbiddenCategoriesCodes.includes(obj.CatCode)
            );

            const finalOrderedTree = getMappedTree(
              allowedCategorisedCubesWithOnlyAllowedCategoriesTree,
              "children",
              node => {
                let ordCount = 1;
                return {
                  ...node,
                  children:
                    node.children
                      ? node.children
                        .sort((a, b) => {
                          if (a.Ord !== undefined && b.Ord !== undefined) {
                            return a.Ord - b.Ord;
                          } else if (a.Ord !== undefined) {
                            return a;
                          } else if (b.Ord !== undefined) {
                            return b;
                          } else {
                            return (a.CatCode || -1) - (b.CatCode || -1);
                          }
                        })
                        .map(c => ({
                          ...c,
                          Ord: c.Ord !== undefined ? String(ordCount++) : undefined
                        }))
                      : node.children
                };
              });

            return {
              ...state,
              categorisedCubes: finalOrderedTree,
              forbiddenCubesCodes,
              forbiddenCategoriesCodes
            };
          case DEFAULT_CATEGORY_SCHEME_SELECTOR_SELECT_SUBMIT:
          case DEFAULT_CATEGORY_SCHEME_SELECTOR_CREATE_SUBMIT:
            return {
              ...state,
              categorisedCubes: null
            };
          case BUILDER_CATEGORY_FORM_CREATE_SUBMIT:
          case BUILDER_CATEGORY_FORM_EDIT_SUBMIT:
          case BUILDER_CUBE_TREE_CATEGORY_MOVE:
            return {
              ...state,
              categorisedCubes: null
            }
          case BUILDER_CUBE_TREE_CATEGORY_DELETE:
            return {
              ...state,
              categorisedCubes: null,
              category: null
            }
          default:
            return state;
        }
      }
      default:
        return state;
    }
  }
});

export default builderReducer;
